import { Link } from 'react-router-dom';
import { ArrowDown, ArrowRight, Plus } from 'lucide-react';
import { T } from '../../i18n/copy';
import { clinicPath } from '../../i18n/paths';
import Reveal from '../Reveal';
import SectionHead from '../SectionHead';
import ImageSlot from '../ImageSlot';

const SLOT_KEYS = ['sales', 'marketing', 'delivery', 'knowledge'];

export default function ValueAreas({ lang }) {
  const t = T.value;
  return (
    <>
      <section id="where-we-create-value" data-header-theme="bone" data-testid="value-section" className="editorial-margins bg-bone pb-12 pt-2 md:pb-16">
        <SectionHead eyebrow={t.eyebrow[lang]} index={t.index} />
        <div className="mt-8 grid grid-cols-1 items-end gap-8 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <h2 className="whitespace-pre-line font-tight font-semibold text-[clamp(2rem,3.6vw,3rem)] leading-[1.06] tracking-[-0.02em] text-midnight">
              {t.headline[lang]}
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5 lg:justify-self-end">
            <p className="max-w-sm text-base leading-relaxed text-midnight/70">{t.intro[lang]}</p>
          </Reveal>
        </div>

        <div className="mt-5 sm:hidden" data-testid="value-areas-mobile">
          {t.areas[lang].map((area, i) => (
            <details key={area.num} data-testid={`value-area-mobile-${i}`} className="group border-t border-midnight/12">
              <summary className="flex cursor-pointer list-none items-center gap-3 py-3.5">
                <ImageSlot slotKey={SLOT_KEYS[i]} compact className="h-[56px] w-[75px] shrink-0" />
                <span className="eyebrow shrink-0 text-vermilion">{area.num}</span>
                <span className="min-w-0 flex-1 text-base font-medium leading-snug text-midnight">{area.title}</span>
                <Plus className="h-4 w-4 shrink-0 text-midnight/40 transition-transform duration-200 group-open:rotate-45" strokeWidth={1.5} />
              </summary>
              <p className="pb-5 pr-2 text-base leading-relaxed text-midnight/70">{area.friction}</p>
            </details>
          ))}
        </div>

        <div className="mt-8 hidden gap-x-8 gap-y-8 sm:grid sm:grid-cols-2 lg:grid-cols-4" data-testid="value-areas">
          {t.areas[lang].map((area, i) => (
            <Reveal key={area.num} delay={i * 0.06} testid={`value-area-${i}`} className="flex gap-4 lg:block">
              <ImageSlot slotKey={SLOT_KEYS[i]} compact className="h-[99px] w-[132px] shrink-0 lg:h-auto lg:w-full" />
              <div className="min-w-0 lg:mt-4">
                <div className="flex items-baseline gap-3 lg:min-h-[41px]">
                  <span className="eyebrow text-vermilion">{area.num}</span>
                  <h3 className="text-base font-medium leading-snug text-midnight">{area.title}</h3>
                </div>
                <p className="mt-1.5 text-base leading-relaxed text-midnight/70 lg:mt-2">{area.friction}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section data-header-theme="midnight" data-testid="value-detail-band" className="bg-midnight text-bone">
        <div className="editorial-margins py-14 md:py-16">
          <div className="hidden gap-x-10 gap-y-10 sm:grid sm:grid-cols-2 lg:grid-cols-4" data-testid="value-details">
            {t.areas[lang].map((area, i) => (
              <Reveal key={area.num} delay={i * 0.06} testid={`value-detail-${i}`}>
                <div className="border-t border-bone/15 pt-5">
                  <span className="eyebrow text-vermilion">{area.num}</span>
                  <h3 className="mt-2 text-base font-medium leading-snug text-bone">{area.title}</h3>
                  <p className="eyebrow mt-4 text-bone/45">{t.labels.change[lang]}</p>
                  <p className="mt-1.5 text-base leading-relaxed text-bone/70">{area.change}</p>
                  <div className="mt-4 flex items-start gap-2 border-t border-bone/15 pt-3">
                    <ArrowDown className="mt-0.5 h-3.5 w-3.5 shrink-0 text-vermilion" strokeWidth={1.5} />
                    <p className="text-base leading-relaxed text-bone/70">{area.outcome}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="sm:hidden" data-testid="value-details-mobile">
            {t.areas[lang].map((area, i) => (
              <details key={area.num} data-testid={`value-detail-mobile-${i}`} className="group border-t border-bone/15">
                <summary className="flex cursor-pointer list-none items-center gap-3 py-4">
                  <span className="eyebrow text-vermilion">{area.num}</span>
                  <span className="flex-1 text-base font-medium text-bone">{area.title}</span>
                  <Plus className="h-4 w-4 shrink-0 text-bone/50 transition-transform duration-200 group-open:rotate-45" strokeWidth={1.5} />
                </summary>
                <div className="pb-5">
                  <p className="eyebrow text-bone/45">{t.labels.change[lang]}</p>
                  <p className="mt-1.5 text-base leading-relaxed text-bone/70">{area.change}</p>
                  <div className="mt-4 flex items-start gap-2 border-t border-bone/15 pt-3">
                    <ArrowDown className="mt-0.5 h-3.5 w-3.5 shrink-0 text-vermilion" strokeWidth={1.5} />
                    <p className="text-base leading-relaxed text-bone/70">{area.outcome}</p>
                  </div>
                </div>
              </details>
            ))}
          </div>

          <div className="mt-12 border-t border-bone/15 pt-8" data-testid="value-invite">
            <span className="inline-block h-2 w-2 bg-vermilion" aria-hidden="true" />
            <div className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-12">
              <h3 className="max-w-md font-tight font-semibold text-[clamp(1.5rem,2.2vw,2rem)] leading-[1.1] tracking-[-0.02em] text-bone lg:col-span-7" data-testid="value-invite-heading">
                {t.invite.heading[lang]}
              </h3>
              <div className="lg:col-span-5">
                <p className="text-base leading-relaxed text-bone/75" data-testid="value-invite-body">
                  {t.invite.body[lang]}
                </p>
                <Link
                  to={clinicPath(lang)}
                  data-testid="value-invite-cta"
                  className="group mt-6 inline-flex items-center gap-3 border-b border-bone/30 pb-1.5 text-[14px] font-medium tracking-[0.04em] text-bone transition-colors duration-300 hover:border-vermilion hover:text-vermilion"
                >
                  {t.invite.cta[lang]}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
